/**
 * Clickable chart legend (WSRADM-0051), shared by the revenue and check-ins
 * charts. Each item is a toggle Button (`aria-pressed` = shown); hidden items
 * are struck through with an outlined swatch, shown items carry a check icon —
 * state is never colour alone. Hiding is display only: no total is recomputed.
 */
import { Box, Button, Tooltip, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { useCopy } from '@/hooks/useCopy';
import type { LegendItem } from './seriesModel';

export interface ChartLegendProps {
  items: readonly LegendItem[];
  onToggle: (key: string) => void;
  onShowAll: () => void;
  /** `data-testid` of the container; items get `${testId}-${label}`. */
  testId: string;
}

export function ChartLegend({ items, onToggle, onShowAll, testId }: ChartLegendProps) {
  const { t } = useCopy();
  const hiddenCount = items.filter((item) => item.hidden).length;
  const visibleCount = items.length - hiddenCount;
  const lastSeries = t('views.lastSeries', 'At least one series stays visible.');

  return (
    <Box
      data-testid={testId}
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 0.5,
        justifyContent: 'center',
        alignItems: 'center',
        mt: 1,
      }}
    >
      {items.map((item) => {
        const isLast = !item.hidden && visibleCount <= 1;
        const button = (
          <Button
            key={item.key}
            size="small"
            variant="text"
            color="inherit"
            aria-pressed={!item.hidden}
            aria-disabled={isLast ? true : undefined}
            onClick={isLast ? undefined : () => onToggle(item.key)}
            data-testid={`${testId}-${item.label}`}
            sx={{ textTransform: 'none', gap: 0.75, minWidth: 0 }}
          >
            <Box
              component="span"
              aria-hidden
              sx={{
                width: 10,
                height: 10,
                borderRadius: '2px',
                flexShrink: 0,
                boxSizing: 'border-box',
                bgcolor: item.hidden ? 'transparent' : item.color,
                border: '2px solid',
                borderColor: item.color,
              }}
            />
            <Typography
              variant="caption"
              sx={{ textDecoration: item.hidden ? 'line-through' : 'none' }}
            >
              {item.label}
            </Typography>
            {item.hidden ? null : <CheckIcon aria-hidden sx={{ fontSize: 14 }} />}
          </Button>
        );

        if (isLast) {
          return (
            <Tooltip key={item.key} title={lastSeries} describeChild>
              {button}
            </Tooltip>
          );
        }
        return button;
      })}
      {hiddenCount > 0 ? (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            {t('views.hiddenSeriesHint', '{{count}} series hidden', { count: hiddenCount })}
          </Typography>
          <Button size="small" variant="text" onClick={onShowAll}>
            {t('views.showAll', 'Show all')}
          </Button>
        </>
      ) : null}
    </Box>
  );
}
